import { useUIStore } from '@/state/store';
import { searchHelp } from '@/data/helpArticles';

interface CmdItem {
  icon: string;
  name: string;
  keys: string[];
  action?: () => void;
}

interface CmdSection {
  label: string;
  items: CmdItem[];
}

// Nur im Gespräch sinnvoll → außerhalb No-Op (Palette schließt trotzdem als Feedback).
function inCall() {
  return useUIStore.getState().view.startsWith('incall');
}
function callAction(fn: () => void) {
  return () => { if (inCall()) fn(); };
}

const SECTIONS: CmdSection[] = [
  {
    label: 'Aktionen im Call',
    items: [
      { icon: '🎤', name: 'Mikrofon stummschalten', keys: ['M'], action: callAction(() => useUIStore.getState().toggleMic()) },
      { icon: '📷', name: 'Kameras tauschen (Webcam ↔ Doc-Cam)', keys: ['1', '2'], action: callAction(() => useUIStore.getState().swapCameras()) },
      { icon: '📸', name: 'Snapshot Doc-Cam', keys: ['P'], action: callAction(() => window.dispatchEvent(new CustomEvent('bamoa:snapshot'))) },
      { icon: '🔍', name: 'Detail-Zoom (Inspection) öffnen', keys: ['I'], action: callAction(() => useUIStore.getState().toggleInspection()) },
      { icon: '⏺', name: 'Aufzeichnung starten', keys: ['R'], action: callAction(() => useUIStore.getState().toggleRecording()) },
    ],
  },
  {
    label: 'Call-Steuerung',
    items: [
      { icon: '⤴', name: 'Einladung · 2. Berater zuschalten', keys: ['⌘', 'E'], action: callAction(() => useUIStore.getState().openEscalate()) },
      { icon: '↪', name: 'Weiterleitung · Call übergeben', keys: ['⌘', 'W'], action: callAction(() => useUIStore.getState().openTransfer()) },
      { icon: '🌐', name: 'Übersetzung umschalten', keys: ['T'], action: callAction(() => useUIStore.getState().toggleTranslation()) },
      { icon: '🚪', name: 'Auflegen', keys: ['⌘', 'H'], action: callAction(() => useUIStore.getState().setView('wrap')) },
    ],
  },
];

export function CmdK() {
  const open = useUIStore((s) => s.cmdkOpen);
  const close = useUIStore((s) => s.closeCmdK);
  const query = useUIStore((s) => s.cmdkQuery);
  const openHelp = useUIStore((s) => s.openHelp);
  const openOnboarding = useUIStore((s) => s.openOnboarding);

  if (!open) return <div className="cmdk-backdrop" />;

  const q = query.trim().toLowerCase();
  const sections = SECTIONS
    .map((sec) => ({ ...sec, items: q ? sec.items.filter((it) => it.name.toLowerCase().includes(q)) : sec.items }))
    .filter((sec) => sec.items.length > 0);

  // Hilfe-Sektion: bei Eingabe passende Artikel, sonst Einstieg „Hilfe öffnen".
  const briefingMatch = !q || 'kurzbriefing erstanmeldung onboarding einführung briefing'.includes(q);
  const helpItems: CmdItem[] = q
    ? searchHelp(q, 6).map((a) => ({ icon: '❓', name: a.title, keys: [], action: () => openHelp(a.slug) }))
    : [{ icon: '❓', name: 'Hilfe öffnen', keys: ['?'], action: () => openHelp() }];
  if (briefingMatch) helpItems.push({ icon: '🚀', name: 'Kurzbriefing (Erstanmeldung) öffnen', keys: [], action: () => openOnboarding() });
  const allSections: CmdSection[] = helpItems.length
    ? [...sections, { label: 'Hilfe', items: helpItems }]
    : sections;

  function run(it: CmdItem) {
    it.action?.();
    close();
  }

  return (
    <div className="cmdk-backdrop active" onClick={(e) => e.target === e.currentTarget && close()}>
      <div className="cmdk" onClick={(e) => e.stopPropagation()}>
        {allSections.length === 0 ? (
          <div className="cmdk-empty">Keine Treffer für „{query}"</div>
        ) : (
          allSections.map((sec) => (
            <div className="cmdk-section" key={sec.label}>
              <div className="cmdk-section-label">{sec.label}</div>
              {sec.items.map((it) => (
                <div className="cmdk-item" key={it.name} onClick={() => run(it)} role="button" tabIndex={0}>
                  <div className="cmdk-item-icon">{it.icon}</div>
                  <div className="cmdk-item-name">{it.name}</div>
                  <div className="cmdk-item-shortcut">
                    {it.keys.map((k, i) => <kbd key={i}>{k}</kbd>)}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}

        <div className="cmdk-footer">
          <span>Pro Cockpit · v1.4</span>
          <div className="cmdk-footer-keys">
            <span><kbd>↑↓</kbd> wählen</span>
            <span><kbd>↵</kbd> ausführen</span>
            <span><kbd>Esc</kbd> schließen</span>
          </div>
        </div>
      </div>
    </div>
  );
}
